import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getApi, postApi, putApi, deleteManyApi } from "common/services/api";
import { toast } from "react-toastify";

const initialState = {
    modules: {}, // Store data for different types dynamically
    loading: false,
    error: null,
};
const user = JSON.parse(localStorage.getItem("user"));

// Async thunk to fetch module data with or without pagination
export const fetchModuleData = createAsyncThunk(
    "dynamicModule/fetchModuleData",
    async ({ moduleId, type, page = 1, fetchAllData = false } = {}, { rejectWithValue }) => {
        try {
            const endpoint = user.role === "superAdmin"
                ? fetchAllData
                    ? `api/form?moduleId=${moduleId}&allData=true`
                    : `api/form?moduleId=${moduleId}&page=${page}`
                : fetchAllData
                    ? `api/form?moduleId=${moduleId}&createBy=${user._id}&allData=true`
                    : `api/form?moduleId=${moduleId}&createBy=${user._id}&page=${page}`;
            const response = await getApi(endpoint);
            return { type, data: response.data };
        } catch (error) {
            return rejectWithValue(error.message || "Failed to fetch module data");
        }
    }
);

// Async thunk to add new data to a module
export const addModuleData = createAsyncThunk(
    "dynamicModule/addModuleData",
    async ({ values }, { rejectWithValue }) => {
        try {
            const response = await postApi("api/form/add", values);
            toast.success("Data added successfully!");
            return response.data;
        } catch (error) {
            toast.error("Failed to add data");
            return rejectWithValue(error.message || "Failed to add data");
        }
    }
);

// Async thunk to edit module data
export const editModuleData = createAsyncThunk(
    "dynamicModule/editModuleData",
    async ({ id, values }, { rejectWithValue }) => {
        try {
            const response = await putApi(`api/form/edit/${id}`, values);
            toast.success("Data updated successfully!");
            return { id, data: response.data };
        } catch (error) {
            toast.error("Failed to update data");
            return rejectWithValue(error.message || "Failed to update data");
        }
    }
);

// Async thunk to delete module data
export const deleteModuleData = createAsyncThunk(
    "dynamicModule/deleteModuleData",
    async ({ ids }, { rejectWithValue }) => {
        try {
            const payload = { ids };
            const response = await deleteManyApi("api/form/deleteMany", payload);
            toast.success("Data deleted successfully!");
            return ids;
        } catch (error) {
            toast.error("Failed to delete data");
            return rejectWithValue(error.message || "Failed to delete data");
        }
    }
);

const dynamicModuleSlice = createSlice({
    name: "dynamicModule",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchModuleData.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchModuleData.fulfilled, (state, action) => {
                state.loading = false;
                state.modules[action.payload.type] = action.payload.data;
            })
            .addCase(fetchModuleData.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(addModuleData.fulfilled, (state, action) => {
                const { type, data } = action.payload;
                if (state.modules[type]) {
                    state.modules[type].data.push(data);
                }
            })
            .addCase(editModuleData.fulfilled, (state, action) => {
                const { type, id, data } = action.payload;
                if (state.modules[type]) {
                    const index = state.modules[type].data.findIndex((item) => item._id === id);
                    if (index !== -1) {
                        state.modules[type].data[index] = data;
                    }
                }
            })
            .addCase(deleteModuleData.fulfilled, (state, action) => {
                const { type, ids } = action.payload;
                if (state.modules[type]) {
                    state.modules[type].data = state.modules[type].data.filter(
                        (item) => !ids.includes(item._id)
                    );
                }
            });
    },
});

export default dynamicModuleSlice.reducer;
